import React from 'react';

/**
 * Three logo variants for review — pass `variant` to switch between them.
 * `size` controls the icon height in px; the wordmark scales accordingly.
 * `theme="light"` = white icon on dark background (sidebar)
 * `theme="dark"`  = coloured icon on white background (login page, etc.)
 */
export type LogoVariant = 'bars' | 'tick' | 'crest';

interface LogoProps {
  variant?: LogoVariant;
  /** Icon height in px — wordmark scales proportionally */
  size?: number;
  /** Controls icon colour palette */
  theme?: 'light' | 'dark';
  /** Show "PM Review" wordmark alongside the icon */
  showWordmark?: boolean;
  className?: string;
}

// ─── Icon A — Rising bars (performance tracking) ─────────────────────────────

function IconBars({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const fill   = theme === 'light' ? '#1e3a8a' : 'white';
  const bar    = theme === 'light' ? 'white'   : '#1e3a8a';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill={fill} />
      {/* Three rising bars + a small trend line */}
      <rect x="7"  y="26" width="7" height="8"  rx="2" fill={bar} opacity="0.6" />
      <rect x="16.5" y="19" width="7" height="15" rx="2" fill={bar} opacity="0.8" />
      <rect x="26" y="11" width="7" height="23" rx="2" fill={bar} />
      {/* Rising trend dots */}
      <circle cx="10.5" cy="24" r="1.5" fill={bar} opacity="0.9" />
      <circle cx="20"   cy="17" r="1.5" fill={bar} opacity="0.9" />
      <circle cx="29.5" cy="9"  r="1.5" fill={bar} />
      <polyline
        points="10.5,24 20,17 29.5,9"
        stroke={bar}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.5"
      />
    </svg>
  );
}

// ─── Icon B — Document with tick (review / confirmation) ─────────────────────

function IconTick({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const fill   = theme === 'light' ? '#1e3a8a' : 'white';
  const stroke = theme === 'light' ? 'white'   : '#1e3a8a';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill={fill} />
      {/* Clipboard / document shape */}
      <rect x="9" y="8" width="22" height="27" rx="3" fill={stroke} opacity="0.12" />
      <rect x="9" y="8" width="22" height="27" rx="3" stroke={stroke} strokeWidth="1.8" />
      {/* Clip top */}
      <rect x="15" y="6" width="10" height="5" rx="2.5" fill={fill} />
      <rect x="15" y="6" width="10" height="5" rx="2.5" stroke={stroke} strokeWidth="1.8" />
      {/* Text lines */}
      <line x1="14" y1="18" x2="26" y2="18" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
      <line x1="14" y1="23" x2="23" y2="23" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
      {/* Big checkmark */}
      <path d="M13 29.5 L17.5 34 L28 24" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Icon C — Shield crest (authority / trust) ───────────────────────────────

function IconCrest({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const fill  = theme === 'light' ? '#1e3a8a' : 'white';
  const inner = theme === 'light' ? 'white'   : '#1e3a8a';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill={fill} />
      {/* Shield path */}
      <path
        d="M20 6 L32 10.5 V22 C32 29 26 34.5 20 37 C14 34.5 8 29 8 22 V10.5 Z"
        fill={inner}
        opacity="0.15"
      />
      <path
        d="M20 6 L32 10.5 V22 C32 29 26 34.5 20 37 C14 34.5 8 29 8 22 V10.5 Z"
        stroke={inner}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      {/* "PM" initials */}
      <text
        x="20"
        y="27"
        textAnchor="middle"
        fontSize="12"
        fontWeight="800"
        fontFamily="system-ui, -apple-system, sans-serif"
        fill={inner}
        letterSpacing="-0.5"
      >
        PM
      </text>
    </svg>
  );
}

// ─── Wordmark ─────────────────────────────────────────────────────────────────

function Wordmark({ size, theme }: { size: number; theme: 'light' | 'dark' }) {
  const bold  = theme === 'light' ? 'text-white'      : 'text-brand-900';
  const light = theme === 'light' ? 'text-brand-200'  : 'text-brand-500';
  const fs    = Math.round(size * 0.45);
  const fsSub = Math.round(size * 0.3);
  return (
    <div className="flex flex-col justify-center leading-none">
      <span className={`font-bold ${bold}`} style={{ fontSize: fs }}>PM Review</span>
      <span className={`font-medium tracking-wide uppercase ${light}`} style={{ fontSize: fsSub }}>
        Performance Management
      </span>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function Logo({
  variant = 'bars',
  size = 40,
  theme = 'light',
  showWordmark = true,
  className = '',
}: LogoProps) {
  const icon = variant === 'bars'  ? <IconBars  size={size} theme={theme} />
             : variant === 'tick'  ? <IconTick  size={size} theme={theme} />
             :                       <IconCrest size={size} theme={theme} />;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {icon}
      {showWordmark && <Wordmark size={size} theme={theme} />}
    </div>
  );
}

/** Icon-only (no wordmark) — convenient shorthand */
export function LogoIcon({
  variant = 'bars',
  size = 40,
  theme = 'light',
  className = '',
}: Omit<LogoProps, 'showWordmark'>) {
  return <Logo variant={variant} size={size} theme={theme} showWordmark={false} className={className} />;
}
